import React, { useState } from 'react';
import { ArrowLeft, Lock, Eye, EyeOff, ShieldCheck, Key, CheckCircle2, ChevronRight, RefreshCw, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router';
import { postChangePassword } from '../../network/APIs/login';


export default function ChangePasswordPage() {
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const navigate = useNavigate()


  // Password visibility triggers
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // Error / Success UI States
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Live password security score helper
  const getPasswordStrength = (pwd) => {
    if (!pwd) return { label: 'Empty', score: 0, color: 'bg-slate-200' };
    let score = 0;
    if (pwd.length >= 6) score += 1;
    if (pwd.length >= 10) score += 1;
    if (/[A-Z]/.test(pwd)) score += 1;
    if (/[0-9]/.test(pwd)) score += 1;
    if (/[^A-Za-z0-9]/.test(pwd)) score += 1;

    if (score <= 2) return { label: 'Weak', score, color: 'bg-rose-500' };
    if (score <= 4) return { label: 'Good', score, color: 'bg-amber-500' };
    return { label: 'Excellent', score, color: 'bg-emerald-500' };
  };

  const strength = getPasswordStrength(newPassword);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!oldPassword || oldPassword.trim() === '') {
      setError('Please provide your current password.');
      return;
    }
    if (!newPassword || newPassword.trim() === '' || newPassword.length < 8) {
      setError('Your new password must be at least 8 characters long.');
      return;
    }

    if (!confirmPassword || confirmPassword.trim() === '' || newPassword !== confirmPassword) {
      setError('Passwords and confirm password do not match. Please verify.');
      return;
    }

    if (newPassword === oldPassword) {
      setError('The new password cannot be identical to your old password.');
      return;
    }


    setIsLoading(true);


    try {
      const user = localStorage.getItem('Freshment_user_data');
      const userData = user ? JSON.parse(user) : null;

      if (!userData.data.id) {
        setError('User not found. Please login again.');
        return;
      }

      const paylaod = {
        user_id: userData?.data?.id,
        oldPassword,
        password: newPassword,
        confirmpassword: confirmPassword
      }
      console.log(paylaod);

      const res = await postChangePassword(paylaod);
      console.log(res);
      if (res.success) {
        setSuccess('Password changed successfully.');
      } else {
        setError('Failed to change password. Please try again.');
      }

    } catch (error) {
      console.error('Error changing password:', error);
      setError('Failed to change password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50/50 font-sans text-slate-900 pb-20 mt-10 sm:mt-5">

      {/* Top Banner Navigation */}
      <div className="bg-white border-b border-slate-100 sticky top-16 z-40 shadow-sm/50">
        <div className="max-w-7xl mx-auto xsm:px-2 sm:px-4 xl:px-6 px-8 h-14 flex items-center justify-between">
          <button
            type="button"
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-slate-500 hover:text-slate-800 text-sm font-extrabold transition-all group"
          >
            <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-0.5" />
            Back to Dashboard
          </button>

          <div className="flex items-center gap-1.5 text-xs text-slate-400 font-bold uppercase tracking-widest bg-slate-50 rounded-lg">
            <Lock className="w-3.5 h-3.5 text-slate-500" />
            Security Preference
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto sm:px-4 xl:px-6 px-8 mt-8 mt-12 sm:mt-6">
        <div className="grid grid-cols-12 xsm:grid-cols-1 gap-8 items-start">

          {/* LEFT COLUMN: Main Form */}
          <div className="col-span-7 xsm:col-span-1 bg-white rounded-[2rem] xl:p-6 p-8 border border-slate-150/80 shadow-[0_4px_25px_rgba(0,0,0,0.02)]">

            {success ? (
              <div className="text-center py-10 px-4 space-y-5 animate-in zoom-in-95 duration-300">
                <div className="inline-flex p-3 bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 rounded-2xl shadow-sm">
                  <ShieldCheck className="w-12 h-12" />
                </div>
                <div className="space-y-2">
                  <h2 className="text-2xl font-black text-slate-900 tracking-tight">Password Changed Successfully</h2>
                  <p className="text-slate-500 text-sm max-w-lg mx-auto leading-relaxed">
                    Your security credentials have been updated. Ensure you use this new password during your subsequent logins.
                  </p>
                </div>

                <div className="pt-4 max-w-sm mx-auto">
                  <button
                    onClick={() => navigate("/")}
                    className="w-full inline-flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-white font-extrabold py-3.5 px-6 rounded-xl text-xs tracking-wide transition active:scale-95 shadow-md"
                  >
                    Return to Dashboard <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <h1 className="text-2xl font-black text-slate-900 tracking-tight mb-2">Change Password</h1>
                  <p className="text-slate-450 text-sm font-medium leading-relaxed">
                    Upgrade your security standards regularly to guard your candidate credentials and job applications.
                  </p>
                </div>

                {error && (
                  <div className="flex items-start gap-2.5 p-4 bg-rose-50 border border-rose-100 rounded-2xl animate-in fade-in duration-200">
                    <AlertCircle className="w-5 h-5 text-rose-500 shrink-0 mt-0.5" />
                    <div className="text-left">
                      <div className="text-xs font-black text-rose-800">Validation Issue</div>
                      <div className="text-xs text-rose-650 font-bold mt-0.5">{error}</div>
                    </div>
                  </div>
                )}

                <div className="space-y-4">
                  {/* Current Password */}
                  <div className="space-y-1.5">
                    <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest block">
                      Current Password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3.5 top-3.5 w-4.5 h-4.5 text-slate-400" />
                      <input
                        type={showOld ? 'text' : 'password'}
                        value={oldPassword}
                        onChange={(e) => setOldPassword(e.target.value)}
                        placeholder="••••••••••••"
                        className="w-full pl-10 pr-10 py-3 rounded-xl border border-slate-200 text-sm font-bold focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all text-slate-800 placeholder-slate-300"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowOld(!showOld)}
                        className="absolute right-3.5 top-3.5 text-slate-400 hover:text-slate-600 transition"
                      >
                        {showOld ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <hr className="border-slate-100" />

                  {/* New Password */}
                  <div className="space-y-1.5">
                    <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest block">
                      New Password
                    </label>
                    <div className="relative">
                      <Key className="absolute left-3.5 top-3.5 w-4.5 h-4.5 text-slate-400" />
                      <input
                        type={showNew ? 'text' : 'password'}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="••••••••••••"
                        className="w-full pl-10 pr-10 py-3 rounded-xl border border-slate-200 text-sm font-bold focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all text-slate-800 placeholder-slate-300"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowNew(!showNew)}
                        className="absolute right-3.5 top-3.5 text-slate-400 hover:text-slate-600 transition"
                      >
                        {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>

                    {/* Interactive password strength indicator */}
                    {newPassword && (
                      <div className="space-y-1.5 pt-1 animate-in slide-in-from-top-1 duration-200">
                        <div className="flex justify-between items-center text-[10px]">
                          <span className="text-slate-450 font-bold">Password Strength rating:</span>
                          <span className="font-extrabold uppercase tracking-wide" style={{ color: strength.score <= 2 ? '#f43f5e' : strength.score <= 4 ? '#f59e0b' : '#10b981' }}>
                            {strength.label}
                          </span>
                        </div>
                        <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden flex gap-0.5">
                          {[1, 2, 3, 4, 5].map((idx) => (
                            <div
                              key={idx}
                              className={`h-full flex-1 transition-all duration-300 ${idx <= strength.score ? strength.color : 'bg-slate-100'}`}
                            />
                          ))}
                        </div>
                        <p className="text-[10px] text-slate-400 leading-normal font-semibold">
                          Include both lowercase & uppercase letters, a number, and a special character for optimal safeguard.
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Confirm Password */}
                  <div className="space-y-1.5">
                    <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest block">
                      Confirm New Password
                    </label>
                    <div className="relative">
                      <Key className="absolute left-3.5 top-3.5 w-4.5 h-4.5 text-slate-400" />
                      <input
                        type={showConfirm ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="••••••••••••"
                        className="w-full pl-10 pr-10 py-3 rounded-xl border border-slate-200 text-sm font-bold focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all text-slate-800 placeholder-slate-300"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirm(!showConfirm)}
                        className="absolute right-3.5 top-3.5 text-slate-400 hover:text-slate-600 transition"
                      >
                        {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full relative py-4 bg-brand-600 hover:bg-brand-700 active:scale-95 text-white font-black rounded-2xl text-xs tracking-wider uppercase transition-all duration-200 shadow-xl shadow-brand-500/20 hover:shadow-brand-500/30 flex items-center justify-center gap-2 group disabled:opacity-75 disabled:pointer-events-none cursor-pointer"
                  >
                    {isLoading ? (
                      <>
                        <RefreshCw className="w-4.5 h-4.5 animate-spin" />
                        Changing...
                      </>
                    ) : (
                      <>
                        Confirm and Update Password
                        <ChevronRight className="w-4.5 h-4.5 text-white/80 transition-transform group-hover:translate-x-1" />
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}

          </div>

          {/* RIGHT COLUMN: Guide & Best Practices */}
          <div className="col-span-5 xsm:col-span-1 space-y-6">

            {/* Security checklist block */}
            <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white rounded-[2rem] p-6 border border-slate-800 shadow-xl overflow-hidden relative">
              <div className="absolute top-0 right-0 w-32 h-32 bg-brand-500/10 rounded-full blur-[40px] pointer-events-none"></div>

              <div className="relative z-10 space-y-5">
                <div className="flex items-center gap-2.5">
                  <div className="p-1.5 bg-brand-500/25 rounded-lg border border-brand-500/30">
                    <ShieldCheck className="w-4.5 h-4.5 text-brand-400" />
                  </div>
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">BEST PRACTICES</span>
                </div>

                <div className="space-y-4">
                  <h3 className="text-base font-black tracking-tight leading-snug">Secure Password Standards</h3>

                  <div className="space-y-3.5">
                    <div className="flex items-start gap-2.5 text-xs font-semibold text-slate-350">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                      <p>Avoid using easily guessable personal info (names, birthdates).</p>
                    </div>

                    <div className="flex items-start gap-2.5 text-xs font-semibold text-slate-350">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                      <p>Combine capitalization, numbers, and symbols like #, ?, %.</p>
                    </div>

                    <div className="flex items-start gap-2.5 text-xs font-semibold text-slate-350">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                      <p>Avoid reusing credentials of active email addresses or social handles.</p>
                    </div>

                    <div className="flex items-start gap-2.5 text-xs font-semibold text-slate-350">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                      <p>Adopt unique combinations with at least 8 to 12 active characters.</p>
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t border-slate-800 text-[10px] font-bold text-slate-500 text-center leading-normal font-mono uppercase tracking-wider">
                  Protected by Enterprise Identity Standards
                </div>
              </div>
            </div>

          </div>

        </div>
      </div>

    </div>
  );
};
